import { createFileRoute } from '@tanstack/react-router';
import React from 'react';
import TablerIconArrowDown from '~icons/tabler/arrow-down';

import { Button, Card, Stack, Typography } from 'gooey';
import { GlowCard } from '#/components/design';
import { Img } from '#/components/images';
import { ButtonLink } from '#/components/links';
import { Page, PageSection } from '#/components/pages';
import fx from '#/fx';
import { Content } from '#/lib/content';
import { canonicalLinkTag, seo } from '#/lib/head';

export const Route = createFileRoute('/_main/careers/')({
  loader: async () => {
    const page = await load();
    return { page };
  },
  head: ({ loaderData, match }) => ({
    meta: seo({
      ...loaderData?.page.seo,
      title: loaderData?.page.seo.title ?? 'Careers',
      description:
        loaderData?.page.seo.description ??
        'Looking for a new opportunity? Join our team and help us build the future.',
      url: match.pathname,
    }),
    links: [canonicalLinkTag(match.pathname)],
  }),
  component: CareersPage,
});

async function load() {
  const page = await fx.getPageContent({
    data: {
      key: 'careers',
    },
  });

  return page;
}

type LoaderData = Awaited<ReturnType<typeof load>>;

function CareersPage() {
  const { page } = Route.useLoaderData();

  const goToRoles: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    const el = document.getElementById('open-roles');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Page>
      <PageSectionHero onCtaClick={goToRoles} sections={page.sections} />
      <PageSectionJoin sections={page.sections} />
      <PageSectionOpenRoles sections={page.sections} />
    </Page>
  );
}

function PageSectionHero({
  sections,
  onCtaClick,
}: CareersPageSectionProps & {
  onCtaClick?: React.MouseEventHandler<HTMLButtonElement>;
}) {
  return (
    <React.Fragment>
      <PageSection className='bg-transparent'>
        <Stack spacing={2} alignItems='center'>
          <Typography variant='h1' align='center'>
            <Content content={sections.hero.title} />
          </Typography>
          <Typography variant='subtitle1' align='center'>
            <Content content={sections.hero.subtitle} />
          </Typography>
          <div className='mt-4'>
            <Button
              onClick={onCtaClick}
              variant='contained'
              size='large'
              endIcon={<TablerIconArrowDown />}
            >
              <Content content={sections.hero.cta} />
            </Button>
          </div>
        </Stack>
      </PageSection>
      <Img
        location='static'
        alt='Collage of team having fun'
        src={sections.hero.cover.src}
        height={sections.hero.cover.height}
        width={sections.hero.cover.width}
        responsive={false}
        className='mx-auto max-lg:mx-3 max-w-(--quark-image-width) w-[90vw] h-auto'
      />
    </React.Fragment>
  );
}

function PageSectionJoin({ sections }: CareersPageSectionProps) {
  return (
    <PageSection>
      <Stack
        spacing={2}
        alignItems='center'
        justifyContent='center'
        className='w-full text-center pb-8'
      >
        <Typography variant='h3' component='h2'>
          <Content content={sections.join.title} />
        </Typography>
        <Typography variant='subtitle1'>
          <Content content={sections.join.subtitle} />
        </Typography>
      </Stack>
      <GlowCard border='top'>
        <Stack spacing={2} className='z-10'>
          <div className='z-10'>
            <Content content={sections.join.content} styling='prose' />
          </div>
        </Stack>
      </GlowCard>
    </PageSection>
  );
}

function PageSectionOpenRoles({ sections }: CareersPageSectionProps) {
  return (
    <PageSection id='open-roles'>
      <Stack spacing={1} alignItems='center' className='w-full'>
        <Typography variant='h3' component='h2'>
          <Content content={sections.openRoles.title} />
        </Typography>
        <Typography variant='subtitle1' align='center'>
          <Content content={sections.openRoles.subtitle} />
        </Typography>
        <div className='w-full pt-4'>
          <Card className='w-full p-8'>
            <Typography>There are no open positions at this time.</Typography>
          </Card>
        </div>
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          spacing={{
            xs: 2,
            sm: 0,
          }}
          alignItems='center'
          className='w-full pt-4 text-center sm:text-left'
        >
          <Typography className='text-gray-400'>
            Didn't see your dream job?
            <br /> Get in touch and let's talk opportunities.
          </Typography>
          <div className='grow' />
          <ButtonLink variant='outlined' size='large' to='/contact'>
            Contact Us
          </ButtonLink>
        </Stack>
      </Stack>
    </PageSection>
  );
}

/*
|------------------
| Internals
|------------------
*/

type CareersPageSectionProps = Pick<LoaderData, 'sections'>;
